import axios, { AxiosError, InternalAxiosRequestConfig } from 'axios';
import { auth } from '../config/firebase';

const API_BASE_URL = import.meta.env.VITE_API_URL;
const API_TIMEOUT = Number(import.meta.env.VITE_API_TIMEOUT_MS);

if (!API_BASE_URL) {
  throw new Error('VITE_API_URL environment variable is required');
}

if (!API_TIMEOUT || isNaN(API_TIMEOUT) || API_TIMEOUT <= 0) {
  throw new Error('VITE_API_TIMEOUT_MS environment variable is required and must be a positive number');
}

const apiClient = axios.create({
  baseURL: `${API_BASE_URL.replace(/\/+$/, '')}/api`,
  timeout: API_TIMEOUT,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor to add Firebase ID token
apiClient.interceptors.request.use(
  async (config: InternalAxiosRequestConfig) => {
    const user = auth.currentUser;
    if (user) {
      try {
        const token = await user.getIdToken();
        config.headers.Authorization = `Bearer ${token}`;
      } catch (error) {
        console.error('Failed to get auth token:', error);
      }
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor for error handling
apiClient.interceptors.response.use(
  (response) => response,
  (error: AxiosError<{ error?: string; message?: string }>) => {
    if (error.response) {
      const status = error.response.status;
      const message =
        error.response.data?.error ||
        error.response.data?.message ||
        'An error occurred';

      switch (status) {
        case 401:
          // Unauthorized - redirect to login (but not if already on the MCP login page)
          if (!window.location.pathname.startsWith('/login/mcp')) {
            window.location.href = '/login';
          }
          break;
        case 403:
          console.error('Forbidden:', message);
          break;
        case 404:
          console.error('Not found:', message);
          break;
        case 429:
          console.error('Rate limited:', message);
          break;
        case 500:
          console.error('Server error:', message);
          break;
        default:
          console.error(`Error ${status}:`, message);
      }

      return Promise.reject(new Error(message));
    }

    if (error.request) {
      return Promise.reject(new Error('Network error. Please check your connection.'));
    }

    return Promise.reject(error);
  }
);

export default apiClient;

// Helper functions for common API calls
export const api = {
  get: <T>(url: string, params?: Record<string, unknown>) =>
    apiClient.get<T>(url, { params }).then((res) => res.data),

  post: <T>(url: string, data?: unknown) =>
    apiClient.post<T>(url, data).then((res) => res.data),

  put: <T>(url: string, data?: unknown) =>
    apiClient.put<T>(url, data).then((res) => res.data),

  patch: <T>(url: string, data?: unknown) =>
    apiClient.patch<T>(url, data).then((res) => res.data),

  delete: <T>(url: string, data?: unknown) =>
    apiClient.delete<T>(url, { data }).then((res) => res.data),
};
